/**
 * useRagVectorConfig Hook
 * Manage the tenant's own Pinecone credentials for RAG — no global fallback,
 * mirrors useLLMConfig.ts's shape.
 */

import adminApi from '@/services/admin.api';
import { useEffect, useState } from 'react';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';

interface RagVectorConfigState {
  configured: boolean;
  indexName: string;
  environment: string;
  isActive: boolean;
  lastValidatedAt?: string | undefined;
  lastValidationStatus: 'valid' | 'invalid' | 'unchecked';
  lastValidationError?: string | undefined;
  ragEnabled: boolean;
  instagramRagEnabled: boolean;
  playgroundRagEnabled: boolean;
  widgetRagEnabled: boolean;
}

/** The three per-channel RAG switches the UI can toggle, mapped to the API's
 *  field names. WhatsApp uses the separate `ragEnabled` field. */
export type RagChannelKey = 'instagramRagEnabled' | 'playgroundRagEnabled' | 'widgetRagEnabled';

const EMPTY_STATE: RagVectorConfigState = {
  configured: false,
  indexName: '',
  environment: '',
  isActive: false,
  lastValidationStatus: 'unchecked',
  ragEnabled: true,
  instagramRagEnabled: true,
  playgroundRagEnabled: true,
  widgetRagEnabled: true,
};

export const useRagVectorConfig = () => {
  const [config, setConfig] = useState<RagVectorConfigState>(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getRagVectorConfig();
      setConfig({
        configured: data.configured,
        indexName: data.indexName || '',
        environment: data.environment || '',
        isActive: data.isActive,
        lastValidatedAt: data.lastValidatedAt,
        lastValidationStatus: data.lastValidationStatus,
        lastValidationError: data.lastValidationError,
        ragEnabled: data.ragEnabled ?? true,
        instagramRagEnabled: data.instagramRagEnabled ?? true,
        playgroundRagEnabled: data.playgroundRagEnabled ?? true,
        widgetRagEnabled: data.widgetRagEnabled ?? true,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch RAG vector configuration');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (apiKey: string | undefined, indexName: string) => {
    setTesting(true);
    setError('');
    try {
      const result = await adminApi.testRagVectorConnection(apiKey, indexName);
      if (!result.valid) {
        setError(result.error || 'Could not connect to Pinecone with these credentials');
      }
      return result;
    } catch (err: any) {
      if (showPlanLockIfTagged(err)) return { valid: false, error: undefined };
      const message = err.message || 'Failed to test connection';
      setError(message);
      return { valid: false, error: message };
    } finally {
      setTesting(false);
    }
  };

  const saveConfig = async (payload: {
    apiKey?: string | undefined;
    indexName: string;
    environment?: string | undefined;
  }) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const data = await adminApi.saveRagVectorConfig(payload);
      setConfig({
        configured: data.configured,
        indexName: data.indexName || '',
        environment: data.environment || '',
        isActive: data.isActive,
        lastValidatedAt: data.lastValidatedAt,
        lastValidationStatus: data.lastValidationStatus,
        lastValidationError: data.lastValidationError,
        ragEnabled: data.ragEnabled ?? true,
        instagramRagEnabled: data.instagramRagEnabled ?? true,
        playgroundRagEnabled: data.playgroundRagEnabled ?? true,
        widgetRagEnabled: data.widgetRagEnabled ?? true,
      });
      setSuccess('Pinecone configuration saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setError(err.message || 'Failed to save Pinecone configuration');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const backfillAgents = async () => {
    setBackfilling(true);
    setError('');
    setSuccess('');
    try {
      const result = await adminApi.backfillRagVectorAgents();
      setSuccess(
        `Resynced ${result.agentsProcessed} agent(s)` +
          (result.agentsFailed > 0 ? ` — ${result.agentsFailed} had errors, check server logs` : '')
      );
      setTimeout(() => setSuccess(''), 5000);
      return result;
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setError(err.message || 'Failed to resync agents');
      throw err;
    } finally {
      setBackfilling(false);
    }
  };

  const updateRagEnabled = async (enabled: boolean) => {
    try {
      setError('');
      await adminApi.updateRagSettings({ ragEnabled: enabled });
      setConfig((prev) => ({ ...prev, ragEnabled: enabled }));
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setError(err.message || 'Failed to update RAG setting');
      throw err;
    }
  };

  /** Toggle one per-channel RAG kill switch (Instagram / Playground / Widget). */
  const updateRagChannel = async (channel: RagChannelKey, enabled: boolean) => {
    try {
      setError('');
      await adminApi.updateRagSettings({ [channel]: enabled });
      setConfig((prev) => ({ ...prev, [channel]: enabled }));
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setError(err.message || 'Failed to update RAG setting');
      throw err;
    }
  };

  return {
    config,
    loading,
    testing,
    backfilling,
    error,
    success,
    saveConfig,
    testConnection,
    backfillAgents,
    refetch: fetchConfig,
    updateRagEnabled,
    updateRagChannel,
  };
};
